import { FlexBox } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { t, type I18nLocale } from '@ardoq/i18n';
import { IconSize, WarningIcon } from '@ardoq/icons';
import { WithPopover } from '@ardoq/popovers';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { colors } from '@ardoq/design-tokens';
import { currentDate } from '@ardoq/date-time';
import { ComponentCellRenderer } from '../../types';
import { PlaceholderValue } from '../../atoms';
import { getFreshness, type LastUpdatedAge } from './freshness';

const getAgeLabel = (i18nLocale: I18nLocale, age: LastUpdatedAge): string => {
  switch (age.unit) {
    case 'today':
      return t(i18nLocale, 'inventory:cellRenderers.freshness.today', 'Today');
    case 'days':
      return t(
        i18nLocale,
        'inventory:cellRenderers.freshness.daysAgo',
        { count: age.count },
        age.count === 1 ? '{{count}} day ago' : '{{count}} days ago'
      );
    case 'weeks':
      return t(
        i18nLocale,
        'inventory:cellRenderers.freshness.weeksAgo',
        { count: age.count },
        age.count === 1 ? '{{count}} week ago' : '{{count}} weeks ago'
      );
    case 'months':
      return t(
        i18nLocale,
        'inventory:cellRenderers.freshness.monthsAgo',
        { count: age.count },
        age.count === 1 ? '{{count}} month ago' : '{{count}} months ago'
      );
    case 'years':
      return t(
        i18nLocale,
        'inventory:cellRenderers.freshness.yearsAgo',
        { count: age.count },
        age.count === 1 ? '{{count}} year ago' : '{{count}} years ago'
      );
  }
};

export const FreshnessCellRenderer: ComponentCellRenderer<unknown> = props => {
  if (!props.data) return <PlaceholderValue />;
  const { i18nLocale } = props.context;
  const freshness = getFreshness(props.data, currentDate());

  if (freshness.status === 'unknown') return <PlaceholderValue />;

  const staleWarning = t(
    i18nLocale,
    'inventory:cellRenderers.freshness.staleWarning',
    'This application has not been updated in over 6 months. The data might not be fresh or accurate.'
  );

  return (
    <FlexBox align="center" gap="xsmall" height="full">
      <Text>{getAgeLabel(i18nLocale, freshness.age)}</Text>
      {freshness.isStale && (
        <>
          <WithPopover content={staleWarning}>
            <WarningIcon iconSize={IconSize.SMALL} color={colors.iconWarning} />
          </WithPopover>
          <VisuallyHidden>{staleWarning}</VisuallyHidden>
        </>
      )}
    </FlexBox>
  );
};
